import React, { Fragment, memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import { DateTime } from 'luxon'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import type { BasicSelectOption } from 'src/components/shared/form/BasicSelect'
import { FormikFileInput } from 'src/components/shared/form/formik/FormikFileInput'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import {
  MICRO_MOBILITY_VEHICLE_TYPES,
  vehicleTypeTitles,
} from 'src/constants/partnerDriver/constants'
import { VehicleType } from 'src/constants/partnerDriver/enums'
import { buildVehicleYearOptions } from 'src/utils/partnerDriver/vehicleYears'
import { completeVehicleInsuranceFormValidator } from './CompleteVehicleInsuranceForm.validator'
import type { CompleteVehicleInsuranceFormValues } from './CompleteVehicleInsuranceForm.interface'

const FORM_ID = 'partner-driver-complete-vehicle-insurance-form'

const INITIAL_VALUES: CompleteVehicleInsuranceFormValues = {
  insuranceDocument: null,
  policyDocument: null,
  vehicleType: '',
  year: '',
  carMake: '',
  carModel: '',
  vin: '',
}

const VEHICLE_TYPE_OPTIONS: BasicSelectOption[] = Object.values(VehicleType).map((vehicleType) => {
  return { label: vehicleTypeTitles[vehicleType], value: vehicleType }
})

interface Props {
  isOpen: boolean
  stepLabel: string
  isSubmitting: boolean
  onSubmit(values: CompleteVehicleInsuranceFormValues): void
  onClose(): void
}

const CompleteVehicleInsuranceModalComponent = ({
  isOpen,
  stepLabel,
  isSubmitting,
  onSubmit,
  onClose,
}: Props) => {
  const yearOptions = useMemo(() => {
    return buildVehicleYearOptions(DateTime.now().year)
  }, [])

  const handleSubmit = useCallback(
    (values: CompleteVehicleInsuranceFormValues) => {
      onSubmit(values)
    },
    [onSubmit],
  )

  return (
    <Formik<CompleteVehicleInsuranceFormValues>
      initialValues={INITIAL_VALUES}
      validationSchema={completeVehicleInsuranceFormValidator}
      onSubmit={handleSubmit}
    >
      {({ values }) => {
        // The required marker follows the backend rule as the vehicle type changes.
        const marker = MICRO_MOBILITY_VEHICLE_TYPES.includes(values.vehicleType as VehicleType)
          ? ''
          : ' *'

        return (
          <CommonModal
            isOpen={isOpen}
            title={`Complete ${stepLabel}`}
            className={tw`max-w-[560px]`}
            onClose={onClose}
            buttons={
              <Fragment>
                <Button size="small" color="grey" disabled={isSubmitting} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  color="dark"
                  type="submit"
                  form={FORM_ID}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  data-testid="partner-driver-vehicle-insurance-submit-button"
                >
                  Submit
                </Button>
              </Fragment>
            }
          >
            <Form id={FORM_ID} css={tw`flex flex-col gap-8`}>
              <div css={tw`flex flex-col gap-4`}>
                <Typography variant="h5">Documents</Typography>
                <FormikFileInput<CompleteVehicleInsuranceFormValues>
                  name="insuranceDocument"
                  label="Insurance document *"
                  inputDataTestId="partner-driver-vehicle-insurance-document-input"
                />
                <FormikFileInput<CompleteVehicleInsuranceFormValues>
                  name="policyDocument"
                  label="Policy document"
                  inputDataTestId="partner-driver-vehicle-policy-document-input"
                />
              </div>
              <div css={tw`flex flex-col gap-4`}>
                <Typography variant="h5">Vehicle</Typography>
                <div css={tw`grid grid-cols-1 gap-4 md:grid-cols-2`}>
                  <FormikSelect<CompleteVehicleInsuranceFormValues>
                    name="vehicleType"
                    label="Vehicle type *"
                    options={VEHICLE_TYPE_OPTIONS}
                    placeholder="Choose a vehicle type"
                  />
                  <FormikSelect<CompleteVehicleInsuranceFormValues>
                    name="year"
                    label={`Year${marker}`}
                    options={yearOptions}
                    placeholder="Select year"
                  />
                  <FormikTextField<CompleteVehicleInsuranceFormValues>
                    name="carMake"
                    label={`Car make${marker}`}
                    placeholder="Car make"
                  />
                  <FormikTextField<CompleteVehicleInsuranceFormValues>
                    name="carModel"
                    label={`Car model${marker}`}
                    placeholder="Car model"
                  />
                </div>
                <FormikTextField<CompleteVehicleInsuranceFormValues>
                  name="vin"
                  label={`VIN${marker}`}
                  placeholder="VIN"
                />
              </div>
            </Form>
          </CommonModal>
        )
      }}
    </Formik>
  )
}

export const CompleteVehicleInsuranceModal = memo(CompleteVehicleInsuranceModalComponent)
